import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Database, ChevronDown, ChevronRight, LogIn, LogOut, User as UserIcon, BookMarked, Shield } from 'lucide-react';
import debounce from 'lodash/debounce';
import { getSetCards } from '../services/pokemonTcgApi';
import { supabase, useProfileStore, isSupabaseConfigured } from '../lib/supabase';
import { useCollectionStore } from '../lib/collectionStore';
import type { User, Profile } from '@supabase/supabase-js';
import { getAllSets } from '../services/pokemonTcgApi';
import type { PokemonSet } from '../types';
import { slugify } from '../utils/slugify';

// Cache key prefix for set data
const SET_CACHE_PREFIX = 'set-';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

// Memory cache for set data
const memoryCache = new Map<string, { data: any; timestamp: number }>();

// Preload set data when hovering over a set
const preloadSetData = debounce(async (setId: string) => {
  try {
    await getSetCards(setId);
  } catch (error) {
    console.error('Error preloading set data:', error);
  }
}, 500);

interface SeriesGroup {
  [key: string]: PokemonSet[];
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const [openSeries, setOpenSeries] = useState<string[]>(['scarlet-violet']);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const { isAuthenticated } = useProfileStore();
  const [sets, setSets] = useState<SeriesGroup>({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.split('/').filter(Boolean);
  const globalProfile = useProfileStore(state => state.profile);
  const resetCollection = useCollectionStore(state => state.reset);
  const resetProfile = useProfileStore(state => state.reset);

  // Close sidebar on navigation on mobile
  useEffect(() => {
    if (window.innerWidth < 1024) {
      onClose();
    }
  }, [location.pathname, onClose]);

  useEffect(() => {
    const fetchSets = async () => {
      try {
        const allSets = await getAllSets();
        const grouped = allSets.reduce((acc: SeriesGroup, set) => {
          if (!acc[set.series]) {
            acc[set.series] = [];
          }
          acc[set.series].push(set);
          return acc;
        }, {});
        setSets(grouped);
      } catch (error) {
        console.error('Error fetching sets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSets();
  }, []);

  useEffect(() => {
    isSupabaseConfigured() && supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        getProfile(session.user.id);
      }
    });

    const {
      data: { subscription } = { subscription: { unsubscribe: () => {} } },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        getProfile(session.user.id);
      } else {
        setProfile(null);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  async function getProfile(userId: string) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      if (data) {
        setProfile(data);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  }

  const handleAuthClick = () => {
    if (user) {
      navigate('/profile');
      onClose();
    } else {
      navigate('/auth');
      onClose();
    }
  };

  const toggleSeries = (series: string) => {
    setOpenSeries(prev => 
      prev.includes(series) 
        ? prev.filter(s => s !== series)
        : [...prev, series]
    );
  };

  const getSetUrl = (set: PokemonSet) => {
    const seriesSlug = slugify(set.series);
    const setSlug = slugify(set.name);
    return `/${seriesSlug}/${setSlug}`;
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error during sign out:', error);
    }
    // Always reset state and navigate, even if sign out fails
    resetProfile();
    resetCollection();
    navigate('/');
    onClose();
  };

  return (
    <div className="h-full w-72 bg-black border-r border-white/10 flex flex-col overflow-hidden">
      <div className="p-4">
        <Link to="/" className="flex items-center gap-2 text-white" onClick={onClose}>
          <div className="relative">
            <Database className="w-7 h-7" />
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-black" />
          </div>
          <div>
            <span className="text-lg font-bold">PokeCard</span>
            <span className="text-lg font-light">Index</span>
            <span className="ml-2 text-xs bg-white/10 text-white/60 px-1.5 py-0.5 rounded-md">alpha</span>
          </div>
        </Link>
      </div>

      <div className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
        <nav className="space-y-1">
          {/* Discord Link */}
          <div className="px-4 py-2">
            <a
              href="https://discord.gg/bE95b6Qg2p"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center gap-3 px-4 py-2 text-white/60 hover:bg-white/5 hover:text-white transition-colors rounded-lg"
            >
              <img 
                src="https://support.discord.com/hc/user_images/PRywUXcqg0v5DD6s7C3LyQ.jpeg"
                alt="Discord"
                className="w-5 h-5 rounded"
              />
              <div>
                <span>Join our Discord</span>
                <p className="text-sm text-white/40">We <span className="animate-heartbeat text-red-500 text-base">♥</span> your feedback!</p>
              </div>
            </a>
          </div>

          {/* Auth/Profile Section */}
          <div className="px-4 py-2 space-y-2">
            {user ? (
              <>
                <button
                  onClick={handleAuthClick}
                  className="w-full flex items-center gap-3 px-4 py-2 text-white/60 hover:bg-white/5 hover:text-white transition-colors rounded-lg group"
                >
                  <div className="relative w-8 h-8 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
                    {globalProfile?.avatar_url ? (
                      <img
                        src={globalProfile.avatar_url}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <UserIcon className="w-full h-full p-1.5 text-white/60" />
                    )}
                  </div>
                  <span className="flex-1 text-left truncate">
                    {globalProfile?.username || user.email}
                  </span>
                </button>
                
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-white/60 hover:bg-white/5 hover:text-white transition-colors rounded-lg ml-11"
                >
                  <LogOut size={20} />
                  <span className="flex-1 text-left">Sign Out</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    navigate('/auth');
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 bg-white/10 hover:bg-white/20 text-white transition-colors rounded-lg"
                >
                  <LogIn size={20} />
                  <span className="flex-1 text-left">Sign In</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/auth?register=true');
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-white/60 hover:bg-white/5 hover:text-white transition-colors rounded-lg"
                >
                  <UserIcon size={20} />
                  <span className="flex-1 text-left">Create Account</span>
                </button>
              </>
            )}
          </div>
          
          {isAuthenticated && (
            <>
            <Link
              to="/collection"
              className={`w-full flex items-center gap-3 px-4 py-2 transition-colors rounded-lg ${
                location.pathname === '/collection'
                  ? 'bg-white/20 text-white font-medium'
                  : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              <BookMarked size={20} />
              <span>My Collection</span>
            </Link>
            </>
          )}

          <div className="my-4 border-t border-white/10"></div>

          {/* Sets Navigation */}
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
            </div>
          ) : (
            Object.entries(sets).map(([series, seriesSets]) => (
              <div key={series}>
                <button
                  onClick={() => toggleSeries(series)}
                  className={`w-full flex items-center gap-3 px-4 py-2 transition-colors rounded-lg cursor-pointer relative isolate ${
                    currentPath[0] === slugify(series)
                      ? 'bg-white/20 text-white font-medium'
                      : openSeries.includes(series)
                      ? 'bg-white/10 text-white'
                      : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <Link
                    to={`/${slugify(series)}`}
                    className="flex-1 text-left"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {series}
                  </Link>
                  {openSeries.includes(series) ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </button>
                {openSeries.includes(series) && (
                  <div className="mt-1 pl-4">
                    {seriesSets.map(set => (
                      <Link
                        key={set.id}
                        to={getSetUrl(set)}
                        onMouseEnter={() => {
                          if (!memoryCache.has(`${SET_CACHE_PREFIX}${set.id}`)) {
                            preloadSetData(set.id);
                          }
                        }}
                        className={`flex items-center gap-2 px-4 py-2 transition-colors rounded-lg group relative isolate ${
                          currentPath[1] === slugify(set.name)
                            ? 'bg-white/20 text-white font-medium'
                            : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }`}
                        onClick={onClose}
                        title={set.name}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate">{set.name}</span>
                            <span className="flex-shrink-0 text-white/40 text-sm">{set.total}</span>
                          </div>
                          {set.name.length > 25 && (
                            <div className="hidden group-hover:block absolute left-0 right-0 mt-1 px-4 py-1 bg-black/90 text-white text-sm rounded-lg">
                              {set.name}
                            </div>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}

          {/* Privacy and Discord Links */}
          <div className="mt-8 px-4">
            <div className="border-t border-white/10 pt-4">
              <Link
                className="w-full flex items-center gap-3 px-4 py-2 text-white/60 hover:bg-white/5 hover:text-white transition-colors rounded-lg"
                to="/privacy"
              >
                <Shield size={20} />
                <span>Privacy Policy</span>
              </Link>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
}